import { useEffect } from "react";
import { DatePicker, Form, Input, Modal, Select } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PRIORITIES, type ActionDto, type NcDto } from "@audit-plan/shared";
import { api } from "../../api/client";
import { usePeople } from "../../api/people";
import { invalidateAudit, useAudit, useQuestions } from "../../api/audits";
import { useFullModal } from "../audit/mobileModal";
import { useIsMobile } from "../useIsMobile";
import { useNotify } from "../audit/useNotify";
import { SensitiveSelect } from "../Sensitive";

interface V {
  title: string;
  description: string;
  ownerId: string;
  assigneeId?: string;
  assigneeName?: string;
  priority: ActionDto["priority"];
  openDate: dayjs.Dayjs;
  startDate?: dayjs.Dayjs | null;
  dueDate: dayjs.Dayjs;
}
const iso = (d?: dayjs.Dayjs | null) => (d ? d.format("YYYY-MM-DD") : null);

/** Nuova azione da NC (nc) o modifica (action). Titolo = punto della checklist, descrizione = rilievo. */
export function ActionFormModal(props: { open: boolean; onClose: () => void; nc?: NcDto; action?: ActionDto; onSaved?: (a: ActionDto) => void }) {
  const { open, onClose, action, onSaved } = props;
  const nc = props.nc;
  const auditId = nc?.auditId ?? action?.auditId;
  const supplierId = nc?.supplierId ?? action?.supplierId;
  const { t, i18n } = useTranslation();
  const fm = useFullModal();
  const mob = useIsMobile();
  const en = i18n.language === "en";
  const [form] = Form.useForm<V>();
  const qc = useQueryClient();
  const n = useNotify();
  const people = usePeople();
  const audit = useAudit(auditId).data;
  const qs = useQuestions(audit?.checklistVersionId).data ?? [];
  const contacts = people.list.filter((p) => p.role === "supplier" && p.supplierId === supplierId);

  useEffect(() => {
    if (!open) return;
    const q = nc?.questionId ? qs.find((x) => x.id === nc.questionId) : undefined;
    const defTitle = q ? `${q.code} · ${en ? q.en || q.it : q.it}` : (nc?.title ?? "");
    form.resetFields();
    form.setFieldsValue({
      title: action?.title ?? defTitle,
      description: action?.description ?? nc?.description ?? "",
      ownerId: action?.ownerId ?? audit?.auditorId,
      assigneeId: action?.assigneeId ?? contacts[0]?.id,
      // sensitive-ok: precompilazione del form, il campo è marcato
      assigneeName: action && !action.assigneeId ? action.assigneeName : "",
      priority: action?.priority ?? "medium",
      openDate: dayjs(action?.openDate ?? undefined),
      startDate: action?.startDate ? dayjs(action.startDate) : null,
      dueDate: action?.dueDate ? dayjs(action.dueDate) : (undefined as unknown as dayjs.Dayjs),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, action?.id, nc?.id, audit?.id, qs.length, contacts.length]);

  const save = useMutation({
    mutationFn: (v: V) => {
      const body = {
        title: v.title,
        description: v.description ?? "",
        ownerId: v.ownerId,
        // sensitive-ok: invio del form
        ...(contacts.length ? { assigneeId: v.assigneeId } : { assigneeId: null, assigneeName: v.assigneeName }),
        priority: v.priority,
        openDate: iso(v.openDate)!,
        startDate: iso(v.startDate),
        dueDate: iso(v.dueDate)!,
      };
      return action ? api.patch<ActionDto>(`/actions/${action.id}`, body) : api.post<ActionDto>(`/ncs/${nc!.id}/actions`, body);
    },
    onSuccess: (r) => {
      n.ok();
      invalidateAudit(qc, auditId);
      onSaved?.(r);
      onClose();
    },
    onError: n.fail,
  });
  const req = { required: true, message: t("required") };
  const hasContacts = contacts.length > 0 || !!action?.assigneeId;
  return (
    <Modal
      {...fm}
      open={open}
      title={action ? `${t("edit")} ${action.code}` : `${t("newAction")}${nc ? ` · ${nc.code}` : ""}`}
      onCancel={onClose}
      okText={t("save")}
      cancelText={t("cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.validateFields().then((v) => save.mutate(v))}
      {...(mob ? {} : { width: 720 })}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Form.Item name="title" label={t("title")} rules={[req]} extra={en ? "From the checklist item" : "Dal punto della checklist"}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label={t("description")} extra={en ? "Finding recorded during the audit" : "Rilievo registrato durante l'audit"}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <div style={{ display: "grid", gridTemplateColumns: mob ? "1fr" : "1fr 1fr", gap: "0 16px" }}>
          <Form.Item name="ownerId" label={t("followedBy")} rules={[req]}>
            <Select options={people.options(["auditor", "manager", "admin"])} showSearch optionFilterProp="label" />
          </Form.Item>
          {hasContacts ? (
            <Form.Item name="assigneeId" label={t("assignee")} rules={[req]}>
              <SensitiveSelect options={contacts.map((p) => ({ value: p.id, label: p.name }))} />
            </Form.Item>
          ) : (
            <Form.Item name="assigneeName" label={t("assignee")} rules={[req]} extra={en ? "No portal user for this supplier yet" : "Il fornitore non ha ancora un'utenza sul portale"}>
              <Input data-sensitive placeholder={en ? "Name and role at the supplier" : "Nome e ruolo presso il fornitore"} />
            </Form.Item>
          )}
          <Form.Item name="priority" label={t("priority")} rules={[req]}>
            <Select options={PRIORITIES.map((p) => ({ value: p, label: t(`pr_${p}`) }))} />
          </Form.Item>
          <Form.Item name="openDate" label={t("openDate")} rules={[req]}>
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="startDate" label={t("startDate")}>
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item
            name="dueDate"
            label={t("dueDate")}
            dependencies={["openDate"]}
            rules={[req, ({ getFieldValue }) => ({ validator: (_, v) => (!v || !getFieldValue("openDate") || !v.isBefore(getFieldValue("openDate"), "day") ? Promise.resolve() : Promise.reject(new Error(t("b_due_before_open")))) })]}
          >
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}
